// hooks/features/annonces/useAnnonces.ts

import { useState, useCallback, useEffect } from "react";
import { Annonce } from "../../types/features/Annonce";
import { annonceService, Recipient } from "../../services/annonceService";


export default function useAnnonces() {
    const [annonces, setAnnonces] = useState<Annonce[]>([]);
    const [selectedAnnonce, setSelectedAnnonce] = useState<Annonce | null>(null);
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        lastPage: 1,
        total: 0,
        perPage: 20,
    });

    // ============================================
    // Récupérer les annonces
    // ============================================
    const getAnnonces = useCallback(async (page: number = 1, perPage: number = 20) => {
        setLoading(true);
        setError(null);
        try {
            const response = await annonceService.getAnnonces(page, perPage);
            setAnnonces(response.data);
            setPagination({
                currentPage: response.current_page,
                lastPage: response.last_page,
                total: response.total,
                perPage: response.per_page,
            });
        } catch (err) {
            setError("Erreur lors du chargement des annonces");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    // ============================================
    // Récupérer une annonce par ID
    // ============================================
    const getAnnonceById = useCallback(async (id: string): Promise<Annonce | null> => {
        setLoading(true);
        setError(null);
        try {
            const annonce = await annonceService.getAnnonceById(id);
            setSelectedAnnonce(annonce);
            return annonce;
        } catch (err) {
            setError("Erreur lors du chargement de l'annonce");
            console.error(err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // ============================================
    // Récupérer les destinataires
    // ============================================
    const getRecipients = useCallback(async (): Promise<Recipient[] | null> => {
        setLoading(true);
        setError(null);
        try {
            const data = await annonceService.getRecipients();
            setRecipients(data);
            return data;
        } catch (err) {
            setError("Erreur lors du chargement des destinataires");
            console.error(err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // ============================================
    // Créer une annonce
    // ============================================
    const createAnnonce = useCallback(async (data: any): Promise<Annonce | null> => {
        setLoading(true);
        setError(null);
        try {
            const newAnnonce = await annonceService.createAnnonce(data);
            setAnnonces((prev) => [newAnnonce, ...prev]);
            return newAnnonce;
        } catch (err) {
            setError("Erreur lors de la création de l'annonce");
            console.error(err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // ============================================
    // Mettre à jour une annonce
    // ============================================
    const updateAnnonce = useCallback(async (id: string, data: any): Promise<Annonce | null> => {
        setLoading(true);
        setError(null);
        try {
            const updatedAnnonce = await annonceService.updateAnnonce(id, data);
            setAnnonces((prev) => prev.map((a) => (a.id === id ? updatedAnnonce : a)));
            if (selectedAnnonce?.id === id) setSelectedAnnonce(updatedAnnonce);
            return updatedAnnonce;
        } catch (err) {
            setError("Erreur lors de la mise à jour de l'annonce");
            console.error(err);
            return null;
        } finally {
            setLoading(false);
        }
    }, [selectedAnnonce]);

    // ============================================
    // Supprimer une annonce
    // ============================================
    const deleteAnnonce = useCallback(async (id: string): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            await annonceService.deleteAnnonce(id);
            setAnnonces((prev) => prev.filter((a) => a.id !== id));
            if (selectedAnnonce?.id === id) setSelectedAnnonce(null);
            return true;
        } catch (err) {
            setError("Erreur lors de la suppression de l'annonce");
            console.error(err);
            return false;
        } finally {
            setLoading(false);
        }
    }, [selectedAnnonce]);

    // ============================================
    // Publier une annonce
    // ============================================
    const publishAnnonce = useCallback(async (id: string): Promise<Annonce | null> => {
        setLoading(true);
        setError(null);
        try {
            const publishedAnnonce = await annonceService.publishAnnonce(id);
            setAnnonces((prev) => prev.map((a) => (a.id === id ? publishedAnnonce : a)));
            if (selectedAnnonce?.id === id) setSelectedAnnonce(publishedAnnonce);
            return publishedAnnonce;
        } catch (err) {
            setError("Erreur lors de la publication de l'annonce");
            console.error(err);
            return null;
        } finally {
            setLoading(false);
        }
    }, [selectedAnnonce]);

    // ============================================
    // INIT
    // ============================================
    useEffect(() => {
        getAnnonces();
    }, [getAnnonces]);

    return {
        // Données
        annonces,
        selectedAnnonce,
        recipients,
        loading,
        error,
        pagination,

        // Méthodes
        getAnnonces,
        getAnnonceById,
        getRecipients,
        createAnnonce,
        updateAnnonce,
        deleteAnnonce,
        publishAnnonce,
    };
}